import { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Badge,
  Box,
  Collapse,
  Fab,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {
  Restore,
  OpenInNew,
  Cancel as CancelIcon,
  Replay,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  Remove,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { restoreDataApp } from "../../services/apps/SmartApps";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { restoreManager, RestoreOp, RestoreOpStatus } from "../../services/restore/RestoreManager";

const ACTIVE: RestoreOpStatus[] = ["queued", "uploading", "restoring"];

function StatusIcon({ status }: { status: RestoreOpStatus }) {
  switch (status) {
    case "completed":
      return <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />;
    case "failed":
      return <ErrorIcon sx={{ color: "error.main", fontSize: 20 }} />;
    case "cancelled":
      return <CancelIcon sx={{ color: "text.disabled", fontSize: 20 }} />;
    default:
      return <Restore sx={{ color: "primary.main", fontSize: 20 }} />;
  }
}

function statusText(op: RestoreOp, t: (k: string) => string): string {
  switch (op.status) {
    case "queued":
      return t("restoreEsicData.queued");
    case "uploading":
      return t("restoreEsicData.uploading");
    case "restoring":
      return t("restoreEsicData.restore.inProgress");
    case "completed":
      return t("restoreEsicData.restored.message");
    case "failed":
      return op.errorMessage || t("restoreEsicData.failed.message");
    case "cancelled":
      return t("restoreEsicData.cancel");
    default:
      return "";
  }
}

export default observer(function RestoreProgressPanel() {
  const navigate = useNavigate();
  const t = (k: string) => translationService.translate(k);
  const [expanded, setExpanded] = useState(true);
  // When `minimized` is true, the panel collapses to a small floating button
  // (FAB) in the corner that can be clicked to bring the panel back.
  const [minimized, setMinimized] = useState(false);

  if (restoreManager.operations.length === 0) return null;

  const activeCount = restoreManager.operations.filter((o) => ACTIVE.includes(o.status)).length;

  if (minimized) {
    return (
      <Fab
        color="primary"
        size="medium"
        sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}
        onClick={() => setMinimized(false)}
        aria-label={t("restoreEsicData.restore.title")}
      >
        <Badge
          badgeContent={restoreManager.operations.length}
          color="error"
          overlap="circular"
        >
          <Restore />
        </Badge>
      </Fab>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: 380,
        zIndex: 1300,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "primary.main",
          color: "white",
          px: 2,
          py: 1,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography variant="subtitle2">
          {t("restoreEsicData.restore.title")}
          {activeCount > 0 ? ` (${activeCount})` : ""}
        </Typography>
        <Box>
          <IconButton
            size="small"
            sx={{ color: "white" }}
            onClick={(e) => {
              e.stopPropagation();
              setMinimized(true);
            }}
            aria-label="minimize"
            title={t("restoreEsicData.minimize")}
          >
            <Remove fontSize="small" />
          </IconButton>
          {activeCount === 0 && (
            <IconButton
              size="small"
              sx={{ color: "white" }}
              onClick={(e) => {
                e.stopPropagation();
                restoreManager.clearFinished();
              }}
              aria-label="close"
            >
              <Close fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" sx={{ color: "white" }}>
            {expanded ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <List dense sx={{ maxHeight: 360, overflow: "auto", py: 0 }}>
          {restoreManager.operations.map((op) => {
            const active = ACTIVE.includes(op.status);
            return (
              <ListItem
                key={op.id}
                sx={{ flexDirection: "column", alignItems: "stretch", py: 1, px: 2 }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <StatusIcon status={op.status} />
                  <ListItemText
                    primary={op.fileName}
                    primaryTypographyProps={{ variant: "body2", noWrap: true }}
                    secondary={statusText(op, t)}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                  <ListItemSecondaryAction sx={{ display: "flex", gap: 0.5 }}>
                    {active && (
                      <IconButton
                        size="small"
                        onClick={() => restoreManager.cancel(op.id)}
                        aria-label={t("restoreEsicData.cancel")}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    )}
                    {op.status === "completed" && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          const svc: any = restoreDataApp?.services?.restoreDataService;
                          // For the currently-resolved op, navigate via the service's route;
                          // for previously-completed ops we have guid/activityType on the op itself.
                          const route =
                            op.restoredGuid && op.restoredActivityType
                              ? routeForActivity(op.restoredActivityType, op.restoredGuid)
                              : svc?.viewDataRoute;
                          if (route) navigate(route);
                        }}
                        aria-label={t("restoreEsicData.viewData")}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    )}
                    {op.status === "failed" && op.file && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => restoreManager.retry(op.id)}
                        aria-label={t("restoreEsicData.tryAgain")}
                      >
                        <Replay fontSize="small" />
                      </IconButton>
                    )}
                    {!active && (
                      <IconButton
                        size="small"
                        onClick={() => restoreManager.dismiss(op.id)}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </Box>
                {active && (
                  <LinearProgress sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                )}
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </Paper>
  );
});

function routeForActivity(activityType: number, guid: string): string {
  switch (activityType) {
    case 1: return `/video-analysis?guid=${guid}`;
    case 2: return `/video-segmentation?guid=${guid}`;
    case 3: return `/video-conversion?guid=${guid}`;
    case 4: return `/inspection-insights?guid=${guid}`;
    case 5: return `/analysis-and-segmentation?guid=${guid}`;
    case 6: return `/analysis-and-inspection-insights?guid=${guid}`;
    default: return "";
  }
}
